import { useState } from 'react';

/**
 * Help Center foundation — a reusable drawer with real, useful content for
 * every module actually implemented in this vertical slice. Deliberately
 * does NOT list sections for unimplemented modules (owner directive: "no
 * dead links" / "only include guides for implemented functionality") —
 * future modules append a new entry to HELP_SECTIONS when they ship, they
 * do not get a placeholder here first.
 */

interface HelpSection {
  id: string;
  title: string;
  body: JSX.Element;
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '0 0 10px', fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', lineHeight: 1.55 }}>{children}</p>;
}

function UL({ items }: { items: string[] }) {
  return (
    <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', lineHeight: 1.6 }}>
      {items.map((it) => (
        <li key={it}>{it}</li>
      ))}
    </ul>
  );
}

const STATIC_SECTIONS: HelpSection[] = [
  {
    id: 'onboarding',
    title: 'Mulai menggunakan ALEX ERP',
    body: (
      <>
        <P>ALEX ERP mengikuti alur operasional standar: data master lebih dulu, lalu siklus pembelian, lalu pergerakan stok.</P>
        <UL
          items={[
            'Master Data — pastikan Items & Suppliers sudah lengkap sebelum membuat dokumen apa pun.',
            'Purchasing — ajukan Purchase Request (PR) → disetujui → jadi Purchase Order (PO).',
            'Goods Receipt — terima barang dari PO, hasilnya langsung memengaruhi Stock Ledger & Stock Balance.',
            'Gunakan "Cabang Aktif" di topbar untuk berpindah konteks cabang — data yang tampil menyesuaikan otomatis.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'master-data',
    title: 'Master Data',
    body: (
      <>
        <P><strong>Items</strong> dan <strong>Suppliers</strong> adalah data tenant-wide (berlaku di semua cabang), bukan per cabang.</P>
        <UL items={['Items: satuan (UoM), kategori, dan status aktif barang.', 'Suppliers: data pemasok yang dipakai saat membuat PO.']} />
      </>
    ),
  },
  {
    id: 'purchasing',
    title: 'Purchasing',
    body: (
      <>
        <P><strong>Purchase Request (PR)</strong> adalah permintaan pembelian dari cabang; setelah disetujui, PR dapat dijadikan <strong>Purchase Order (PO)</strong> ke supplier.</P>
        <UL items={['PR & PO bersifat per cabang — beralih Cabang Aktif menampilkan dokumen cabang tersebut.', 'Status PR/PO mengikuti alur approval real, bukan status manual.']} />
      </>
    ),
  },
  {
    id: 'goods-receipt',
    title: 'Goods Receipt',
    body: <P>Goods Receipt (GR) mencatat barang yang diterima dari PO. Setiap GR yang diposting langsung menulis baris baru ke Stock Ledger (append-only) dan memperbarui Stock Balance gudang tujuan.</P>,
  },
  {
    id: 'inventory',
    title: 'Inventory',
    body: (
      <>
        <P><strong>Stock Ledger</strong> adalah riwayat pergerakan stok yang immutable — setiap baris adalah transaksi nyata (GR, transfer, waste, opname), tidak pernah diedit setelah tercatat.</P>
        <P><strong>Stock Balance</strong> adalah saldo per gudang, hasil proyeksi dari Stock Ledger — bukan angka yang diinput manual.</P>
      </>
    ),
  },
  {
    id: 'stock-opname',
    title: 'Stock Opname',
    body: <P>Sesi hitung fisik stok. Selisih antara hasil hitung dan Stock Balance saat itu, setelah diposting, ditulis sebagai baris penyesuaian baru ke Stock Ledger.</P>,
  },
  {
    id: 'transfers',
    title: 'Transfers',
    body: <P>Perpindahan stok antar gudang dalam cabang yang sama atau lintas cabang. Setiap transfer yang diposting menulis dua baris ke Stock Ledger: pengurangan di gudang asal, penambahan di gudang tujuan.</P>,
  },
  {
    id: 'waste',
    title: 'Waste',
    body: <P>Pencatatan barang rusak atau tidak layak jual. Waste yang diposting mengurangi Stock Balance gudang terkait dan tercatat permanen di Stock Ledger.</P>,
  },
  {
    id: 'expiry',
    title: 'Expiry / Lot',
    body: <P>Pelacakan lot dan tanggal kedaluwarsa barang yang dikelola per batch, untuk membantu rotasi stok (FEFO) dan mencegah barang kedaluwarsa terjual.</P>,
  },
  {
    id: 'menu-master',
    title: 'Menu Master',
    body: (
      <>
        <P><strong>Menu</strong> adalah produk jual (mis. hidangan) — data tenant-wide, bukan per cabang. Setiap menu punya kode, nama, status aktif, dan riwayat harga jual (berlaku per tanggal).</P>
        <UL items={['Menu adalah dasar untuk membuat Resep dan menghitung HPP.', 'Mengubah harga jual tidak menghapus harga lama — riwayat harga tersimpan lengkap.']} />
      </>
    ),
  },
  {
    id: 'recipe',
    title: 'Recipe / BOM',
    body: (
      <>
        <P>Setiap menu bisa punya satu <strong>Resep</strong> (bill of materials): daftar bahan baku, jumlah per hasil (yield), dan satuan.</P>
        <UL
          items={[
            'Resep bersifat versi (recipe version) — membuat resep baru tidak menghapus yang lama, versi sebelumnya otomatis ditandai "SUPERSEDED" dengan tanggal berlakunya.',
            'Bahan baku resep mengacu ke Items yang sama dengan modul Purchasing/Inventory.',
            'Satu bahan hanya boleh muncul sekali dalam satu resep.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'hpp',
    title: 'HPP',
    body: (
      <>
        <P>HPP (harga pokok produksi) dihitung otomatis dari resep menggunakan metode <strong>harga pembelian terakhir</strong> (last purchase price) per bahan — bukan asumsi yang bisa diubah dari layar ini.</P>
        <UL
          items={[
            'Jika suatu bahan belum pernah dibeli/diterima (belum ada Goods Receipt), harga bahan tersebut ditandai "Belum tersedia" dan HPP ditandai PERKIRAAN.',
            'HPP adalah snapshot per waktu hitung — tekan "Hitung Ulang HPP" untuk memperbarui setelah harga bahan berubah.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'production',
    title: 'Produksi',
    body: (
      <>
        <P><strong>Batch Produksi</strong> mencatat pembuatan suatu menu dalam jumlah tertentu di satu cabang & gudang. Saat diposting, bahan baku sesuai resep otomatis dikurangi dari Stock Balance gudang tersebut dan tercatat di Stock Ledger.</P>
        <UL
          items={[
            'Batch berstatus DRAFT dapat diperiksa dulu sebelum diposting; setelah POSTED, batch tidak bisa diposting ulang.',
            'Posting akan ditolak jika stok bahan baku tidak mencukupi.',
            'Menu yang diproduksi TIDAK menambah stok tersendiri — produksi ini menghitung konsumsi bahan & HPP, bukan menggudangkan hidangan jadi.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'employees',
    title: 'Karyawan',
    body: (
      <>
        <P>Roster karyawan — dasar untuk Kehadiran, Cuti, Tukar Shift, dan Disiplin. Data karyawan (No., Nama, Cabang, Divisi, Status) berbeda dari akun login (User) — satu karyawan bisa punya akun login atau tidak.</P>
        <UL
          items={[
            'Jabatan/posisi dan data kontak (email/telepon) belum tersedia di skema saat ini — tidak ditampilkan.',
            'Data kompensasi (gaji/payroll) tidak ditampilkan di halaman ini — memerlukan akses terpisah.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'shifts',
    title: 'Shift Master',
    body: (
      <>
        <P>Definisi shift kerja: kode, nama, jam mulai, dan jam selesai. Ini adalah dasar untuk Jadwal Kerja (Schedule) — setiap jadwal karyawan mengacu ke satu shift.</P>
        <UL
          items={[
            'Shift lintas hari (mis. 22:30 → 06:30) didukung penuh — sistem mendeteksi otomatis dari jam mulai/selesai dan menandainya "lintas hari", bukan menolaknya sebagai input tidak valid.',
            'Shift bersifat per-cabang, mengikuti Cabang Aktif.',
            'Hanya HR dan Tenant Owner yang dapat membuat/mengubah shift — role lain (Manager/Supervisor/Leader) hanya dapat melihat.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'schedule',
    title: 'Jadwal Kerja',
    body: (
      <>
        <P>Penjadwalan karyawan ke shift tertentu pada tanggal tertentu. Ini adalah dasar untuk pencatatan Kehadiran — clock-in/out memerlukan jadwal PLANNED yang sudah ada.</P>
        <UL
          items={[
            'Membuat, mereassign, atau membatalkan jadwal selalu lewat jalur RPC bertingkat izin — tidak ada penulisan langsung ke tabel dari browser.',
            'Jadwal yang sudah punya kehadiran tercatat tidak bisa direassign/dibatalkan lagi — riwayat kehadiran nyata tidak boleh menjadi tidak konsisten.',
            'Menyetujui pengajuan Cuti yang tumpang tindih dengan jadwal PLANNED akan otomatis membatalkan jadwal tersebut (dampak ke ketersediaan/coverage).',
            'Karyawan pengganti pada reassign/tukar shift harus berada di cabang yang sama dengan jadwal — lintas cabang diblokir di server.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'attendance',
    title: 'Kehadiran',
    body: (
      <>
        <P>Riwayat clock-in/clock-out karyawan. <strong>Keterlambatan</strong> dihitung otomatis oleh server dari kebijakan keterlambatan (attendance policy) nyata yang berlaku — bukan perkiraan.</P>
        <UL
          items={[
            'Mencatat kehadiran memerlukan jadwal (schedule) yang sudah ada untuk karyawan tersebut.',
            'Jika belum ada kebijakan keterlambatan aktif untuk cabang, pencatatan clock-in akan ditolak sampai kebijakan dibuat.',
            'Fakta kehadiran yang sudah tercatat tidak dapat diedit/dihapus langsung (diblokir di level database). HR/Owner dapat mengajukan "Koreksi" dengan alasan — ini hanya mencatat permintaan koreksi sebagai bukti audit, catatan asli tetap utuh.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'leave',
    title: 'Cuti',
    body: <P>Pengajuan dan persetujuan cuti karyawan (Sakit, Tahunan, Izin, Lainnya). Tanggal selesai harus sama atau setelah tanggal mulai. Tidak ada aturan saldo cuti (leave balance) yang diberlakukan — skema belum mendefinisikannya.</P>,
  },
  {
    id: 'shift-swap',
    title: 'Tukar Shift',
    body: <P>Karyawan meminta jadwal kerjanya ditukar dengan karyawan lain. Setelah disetujui, jadwal (schedule) yang bersangkutan otomatis dipindahkan ke karyawan pengganti.</P>,
  },
  {
    id: 'disciplinary',
    title: 'Disiplin',
    body: (
      <P>
        Tindakan disiplin karyawan (Peringatan, Skorsing, Pemutusan, Penghargaan, Lainnya) — data sensitif, hanya dapat dilihat oleh HR dan Owner. Pembatasan ini diberlakukan di server (database), bukan hanya disembunyikan di tampilan.
      </P>
    ),
  },
  {
    id: 'finance-coa',
    title: 'Chart of Accounts',
    body: (
      <>
        <P>Daftar akun akuntansi (Aset, Liabilitas, Ekuitas, Pendapatan, Beban) — dasar untuk setiap posting jurnal. Akun bersifat tenant-wide, tidak per-cabang.</P>
        <UL items={['Akun non-postable (header/grup) hanya untuk pengelompokan — tidak bisa dipakai langsung di baris jurnal.', 'Hanya HR/Finance dan Owner yang dapat membuat/mengubah akun.']} />
      </>
    ),
  },
  {
    id: 'finance-payroll',
    title: 'Payroll',
    body: (
      <>
        <P>Alur payroll mengikuti 3 tahap nyata: Periode → Fakta Gaji (earning/potongan per karyawan) → Payroll Run (kumpulkan fakta APPROVED menjadi baris gaji per karyawan) → Posting ke Jurnal.</P>
        <UL
          items={[
            'Fakta gaji berstatus DRAFT tidak pernah ikut dibayar — harus disetujui (APPROVED) oleh Finance/Owner terlebih dahulu, dan penyetuju tidak boleh orang yang sama dengan pencatat (maker-checker).',
            'Tidak ada struktur gaji otomatis di skema saat ini — jumlah gaji pokok, bonus, dan potongan selalu diinput manual, meski bisa merujuk ke kejadian HR nyata (keterlambatan, cuti).',
            'Satu Payroll Run per cabang per periode — run duplikat akan ditolak server.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'finance-ap',
    title: 'Accounts Payable',
    body: (
      <>
        <P>Invoice supplier (AP) dan pembayaran keluar. Invoice yang merujuk ke Goods Receipt harus benar-benar ada dan berstatus POSTED — tidak bisa membuat invoice fiktif dari halaman ini.</P>
        <UL items={['Pembayaran yang melebihi sisa saldo invoice akan ditolak server.', 'Setiap invoice/pembayaran melalui alur Draft → Setujui (maker-checker) → Posting ke Jurnal.']} />
      </>
    ),
  },
  {
    id: 'finance-journals',
    title: 'Jurnal & Periode Akuntansi',
    body: (
      <>
        <P>Setiap jurnal wajib double-entry (total debit harus sama dengan total kredit) — server menolak jurnal yang tidak seimbang, entry nol, atau posting ke periode yang sudah ditutup.</P>
        <UL
          items={[
            'Jurnal yang sudah diposting tidak pernah diedit langsung — koreksi selalu lewat "Balik Jurnal" (reversal), yang membuat jurnal baru berlawanan dan menandai jurnal asli sebagai REVERSED.',
            'Menutup periode akan diblokir jika trial balance periode tersebut belum nol (tidak seimbang) — pemeriksaan dilakukan di server.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'finance-reports',
    title: 'Laporan Keuangan',
    body: (
      <>
        <P>Trial Balance dihitung langsung dari jurnal terposting per periode. Laba Rugi dan Neraca (draft) diturunkan dari data yang sama — bukan laporan tutup buku resmi.</P>
        <UL
          items={[
            'Arus Kas belum tersedia — skema belum punya klasifikasi akun ke aktivitas operasi/investasi/pendanaan.',
            'Rekonsiliasi Bank belum terhubung — tidak ada sumber feed bank/kas nyata di skema saat ini, jadi tidak ada saldo yang dikarang.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'assets',
    title: 'Aset',
    body: (
      <>
        <P>Register aset per cabang — tag aset harus unik per tenant (ditolak server jika duplikat). Riwayat (perolehan, perubahan kondisi, perawatan, pelepasan) bersifat append-only — tidak bisa diedit/dihapus.</P>
        <UL
          items={[
            'Pelepasan aset (disposal) memakai maker-checker: yang mengajukan tidak boleh menjadi orang yang sama yang menyetujui — ditolak di server jika sama.',
            'Aset yang sudah DISPOSED tidak bisa dikembalikan ke ACTIVE lewat edit biasa, dan tidak bisa dibuatkan work order baru.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'maintenance',
    title: 'Perawatan Aset',
    body: (
      <>
        <P>Work order perawatan: Terbuka → (Ditugaskan) → Berjalan → Selesai/Dibatalkan. Jadwal preventif menghitung jatuh tempo dari interval hari nyata yang Anda input — tidak ada interval yang dikarang sistem.</P>
        <UL items={['Work order tidak bisa dibuat untuk aset yang sudah dilepas (DISPOSED).', 'AI (jika ada) hanya boleh menyarankan/menandai — tidak pernah menutup work order, mengubah kondisi, atau menyetujui biaya secara otomatis.']} />
      </>
    ),
  },
  {
    id: 'crm',
    title: 'CRM — Pelanggan',
    body: (
      <>
        <P>Identitas pelanggan terpisah dari akun karyawan/ERP, bersifat tenant-wide (tidak per-cabang). Consent (Marketing/WhatsApp) bersifat append-only — status opt-out tidak pernah otomatis diaktifkan kembali.</P>
        <UL
          items={[
            'Duplikasi identitas (no. telepon/email yang sama di 2 pelanggan) TIDAK PERNAH digabung otomatis — hanya ditandai untuk Review Duplikasi, dan penggabungan memerlukan keputusan manusia yang berbeda dari yang menandai (maker-checker).',
            'Program loyalitas transaksional (poin/reward/kupon) belum aktif — menyusul bersama modul POS.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'command-center',
    title: 'Command Center',
    body: (
      <>
        <P>KPI lintas modul dihitung langsung dari sumber data nyata (metric_registry/report_snapshots) — tidak ada angka penjualan/kas/POS (menyusul bersama modul POS), dan metrik yang tidak berwenang Anda lihat (mis. Payroll tanpa izin payroll_fact.read) disembunyikan sepenuhnya, bukan ditampilkan sebagai 0.</P>
        <UL
          items={[
            'Total pada setiap kartu KPI dan rincian drill-down-nya dihitung dari query yang sama dalam satu pemanggilan — total tidak mungkin menyimpang dari rinciannya.',
            'Pandangan "Semua Cabang (Konsolidasi)" hanya tersedia untuk peran dengan hak akses tenant-wide (mis. Owner) — Manager yang terikat satu cabang tidak bisa mengaksesnya, ditolak di server, bukan hanya disembunyikan di tampilan.',
            'Status "Awal (Preliminary)" berarti periode akuntansi terkait masih terbuka — angka nyata, tapi belum final; tidak pernah otomatis dinaikkan menjadi Final tanpa periode ditutup.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'reports',
    title: 'Report Center',
    body: (
      <>
        <P>Generasi laporan Harian/Bulanan menulis satu report_run + snapshot metrik yang immutable — snapshot yang sudah dibuat tidak pernah berubah lagi meski data sumber berubah setelahnya; generate ulang untuk periode yang sama membuat report_run baru dengan angka terkini.</P>
        <UL
          items={[
            'Ekspor CSV/PDF selalu terikat ke satu report_run tertentu (nama file memuat ID-nya) — bukan hasil query ulang saat diunduh.',
            'Laporan yang gagal (mis. karena guard rekonsiliasi mendeteksi ketidaksesuaian) ditandai Gagal dengan alasan nyata — sistem tidak pernah menampilkan sukses palsu.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'notifications',
    title: 'Notifikasi / WhatsApp',
    body: (
      <>
        <P>Antrian pengiriman (notification_outbox) bersifat idempotent — permintaan dengan kunci logis yang sama (retry/klik ganda) selalu mengembalikan notifikasi kanonik yang sama, tidak pernah dua kali. Setiap pengiriman melalui SANDBOX_MOCK_PROVIDER internal (deterministik, tidak ada panggilan jaringan nyata) — belum ada kredensial provider WhatsApp produksi, jadi "Terkirim (SANDBOX)" tidak pernah berarti benar-benar sampai ke WhatsApp asli.</P>
        <UL
          items={[
            'Percobaan pengiriman (delivery_attempts) adalah bukti immutable — percobaan yang gagal tidak pernah dihapus/ditimpa, hanya percobaan baru yang ditambahkan.',
            'Kegagalan sementara (timeout, rate limit) dicoba ulang secara terbatas (maksimum 3 percobaan) sebelum masuk Dead-Letter; kegagalan permanen (nomor tidak valid, diblokir kebijakan) langsung masuk Dead-Letter tanpa mencoba ulang.',
            'Nomor/alamat penerima penuh hanya terlihat oleh peran dengan izin notification.route_manage pada cabang terkait — peran lain melihat versi tersamar.',
            'Notifikasi pemasaran ke pelanggan tunduk pada consent CRM (opt-out) — tanpa consent MARKETING yang aktif, permintaan tercatat sebagai Diblokir, tidak pernah dikirim.',
            'Outbound Kill Switch bersifat server-authoritative dan teraudit — saat aktif, permintaan baru tetap tercatat tapi tidak pernah benar-benar memanggil provider.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'ai-analyst',
    title: 'AI Analyst',
    body: (
      <>
        <P>
          Dijawab oleh DEV_PROVIDER internal — mesin aturan deterministik (keyword→maksud→tool), BUKAN model bahasa. Tidak ada kredensial
          penyedia AI produksi (Anthropic/OpenAI/dll) di lingkungan ini; jawaban hanya pernah menyusun ulang hasil pemanggilan tool nyata
          (kehadiran, stok, purchase order, laba rugi, laporan, metrik terdaftar) — tidak pernah mengarang angka. Bila pertanyaan tidak cocok
          dengan tool manapun, jawabannya selalu "Data Tidak Tersedia", bukan tebakan.
        </P>
        <UL
          items={[
            'AI tidak pernah menulis data ERP secara langsung — pertanyaan bernuansa tindakan hanya pernah menghasilkan usulan (ai_proposals) berstatus Menunggu Persetujuan.',
            'Persetujuan usulan mengecek ulang izin domain ASLI (mis. maintenance_order.create, purchase_request.create) — kepemilikan izin ai.approve saja tidak cukup.',
            'Hanya dua jenis usulan yang benar-benar mengeksekusi fungsi domain kanonik saat disetujui (Perintah Maintenance, Pemesanan Ulang Stok); dua jenis lainnya (Investigasi Kehadiran, Tinjauan Varians Keuangan) bersifat informasional saja karena belum ada mutasi atomik yang didefinisikan untuk itu.',
            'Cakupan tool AI tidak pernah melebihi cakupan ERP pengguna sendiri — setiap tool memanggil gerbang izin (fn_check_permission) yang sama persis dengan yang dipakai halaman biasa.',
            'Setiap jawaban menampilkan panel Bukti (tool yang dipanggil, status, waktu data) — bedakan FAKTA (dari tool) dengan interpretasi/usulan AI.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'faq',
    title: 'FAQ',
    body: (
      <>
        <FaqItem q="Kenapa data yang saya lihat berubah saat ganti Cabang Aktif?">
          Karena modul tersebut memang per-cabang (PR, PO, GR, Stock Ledger/Balance, Kehadiran, Cuti, Tukar Shift, Payroll Run, AP, dsb). Items, Suppliers, Chart of Accounts, dan Periode
          tidak berubah karena semuanya data tenant-wide.
        </FaqItem>
        <FaqItem q="Kenapa saya tidak bisa mengedit baris Stock Ledger?">
          Stock Ledger bersifat append-only by design — riwayat transaksi tidak boleh diubah, hanya bisa ditambah baris koreksi baru (mis. lewat Stock Opname).
        </FaqItem>
        <FaqItem q="Kenapa jurnal yang salah tidak bisa saya edit langsung?">
          Jurnal yang sudah diposting bersifat immutable by design, sama seperti Stock Ledger — koreksi selalu lewat "Balik Jurnal" (reversal) agar riwayat akuntansi tetap utuh dan bisa
          diaudit.
        </FaqItem>
        <FaqItem q="Kenapa saya tidak melihat cabang lain di daftar Cabang Aktif?">
          Selektor hanya menampilkan cabang yang benar-benar Anda punya akses — ini otorisasi server (RLS), bukan pembatasan tampilan saja.
        </FaqItem>
        <FaqItem q="Kenapa HPP menu saya menunjukkan 'Belum tersedia' atau berlabel PERKIRAAN?">
          Karena satu atau lebih bahan di resep tersebut belum pernah punya Goods Receipt yang diposting, jadi belum ada harga pembelian nyata untuk dihitung.
        </FaqItem>
        <FaqItem q="Kenapa saya tidak bisa mencatat kehadiran untuk seorang karyawan?">
          Karyawan tersebut belum punya jadwal (schedule) PLANNED, atau Anda tidak memiliki izin attendance_event.create di cabang tersebut.
        </FaqItem>
        <FaqItem q="Kenapa shift 22:30 → 06:30 tidak ditolak sebagai jam selesai sebelum jam mulai?">
          Karena itu memang shift lintas hari (malam ke pagi) yang sah — sistem menghitungnya sebagai "lintas hari" secara otomatis, bukan kesalahan input. Perhitungan keterlambatan tetap mengacu ke tanggal kerja shift dimulai.
        </FaqItem>
        <FaqItem q="Kenapa saya tidak bisa mengedit langsung data kehadiran yang salah?">
          Fakta kehadiran (attendance_events) bersifat immutable by design — tidak bisa diubah/dihapus langsung, bahkan oleh HR. Gunakan aksi "Koreksi" untuk mencatat permintaan koreksi beserta alasannya; ini menjadi bukti audit, bukan menimpa data asli.
        </FaqItem>
        <FaqItem q="Kenapa mendaftarkan aset dengan tag yang sama ditolak?">
          Tag aset harus unik per tenant — ini diberlakukan di database (bukan hanya validasi form), sehingga tidak mungkin ada dua aset dengan tag yang sama meski dua orang mencoba mendaftarkan bersamaan.
        </FaqItem>
        <FaqItem q="Kenapa dua pelanggan dengan nomor telepon yang sama tidak otomatis digabung?">
          Sistem sengaja tidak pernah menggabungkan otomatis — hanya menandai sebagai kandidat duplikasi untuk direview manusia. Ini mencegah penggabungan keliru yang bisa kehilangan riwayat interaksi pelanggan.
        </FaqItem>
      </>
    ),
  },
  {
    id: 'shortcuts',
    title: 'Keyboard shortcuts',
    body: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ShortcutRow keys="Ctrl / Cmd + K" desc="Buka pencarian cepat (command palette)" />
        <ShortcutRow keys="Esc" desc="Tutup panel/drawer yang sedang terbuka" />
      </div>
    ),
  },
];

function FaqItem({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)', marginBottom: 2 }}>{q}</div>
      <div style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

function ShortcutRow({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)' }}>
      <span>{desc}</span>
      <kbd style={{ border: '1px solid var(--alex-border)', borderRadius: 4, padding: '2px 6px', fontSize: 10.5, color: 'var(--alex-text-primary)' }}>{keys}</kbd>
    </div>
  );
}

export function HelpCenter({
  open,
  onClose,
  currentPageTitle,
  currentPageHelp,
}: {
  open: boolean;
  onClose: () => void;
  currentPageTitle: string;
  currentPageHelp: string;
}) {
  const sections: HelpSection[] = [
    { id: 'this-page', title: 'Panduan halaman ini', body: <P>{currentPageHelp}</P> },
    ...STATIC_SECTIONS,
  ];
  const [activeId, setActiveId] = useState('this-page');
  const active = sections.find((s) => s.id === activeId) ?? sections[0];

  if (!open) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', zIndex: 40 }} />
      <div
        role="dialog"
        aria-label="Help Center"
        className="alex-card"
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 480,
          maxWidth: '92vw',
          zIndex: 41,
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--alex-shadow-popover)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid var(--alex-border)' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--alex-text-primary)' }}>Help Center</div>
            <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)' }}>{currentPageTitle}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Tutup"
            style={{ border: 'none', background: 'none', fontSize: 18, color: 'var(--alex-text-muted)', cursor: 'pointer', padding: 4 }}
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <nav style={{ width: 176, flexShrink: 0, borderRight: '1px solid var(--alex-border)', overflowY: 'auto', padding: 8 }}>
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  marginBottom: 2,
                  border: 'none',
                  borderRadius: 'var(--alex-control-radius)',
                  background: active.id === s.id ? 'var(--alex-primary-soft)' : 'transparent',
                  color: active.id === s.id ? 'var(--alex-primary)' : 'var(--alex-text-secondary)',
                  fontWeight: active.id === s.id ? 700 : 500,
                  fontSize: 'var(--alex-fs-micro)',
                  cursor: 'pointer',
                }}
              >
                {s.title}
              </button>
            ))}
          </nav>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-primary)', marginBottom: 10 }}>{active.title}</div>
            {active.body}
          </div>
        </div>
        <div style={{ padding: '14px 18px', borderTop: '1px solid var(--alex-border)' }}>
          <a href="mailto:support@alex-erp.dev" style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-primary)', fontWeight: 700, textDecoration: 'none' }}>
            Hubungi Support →
          </a>
        </div>
      </div>
    </>
  );
}
